import { createEffect, type Effect } from 'effector'
import { z } from 'zod'

import { logger } from '@portal/logger'

import { ChatAPIError, ChatError, type HTTPResponse } from './types'

export type ChatAPIEffect<TRequest, TResponse> = Effect<TRequest, TResponse, ChatAPIError>

export function createChatAPIEffect<TRequest, TResponseSchema extends z.ZodType<any, any, any>>(
    validator: TResponseSchema,
    requestFn: (request: TRequest) => Promise<HTTPResponse<{ data: z.TypeOf<typeof validator> }>>
): ChatAPIEffect<TRequest, z.TypeOf<typeof validator>>

export function createChatAPIEffect<TRequest, TResponse>(
    requestFn: (request: TRequest) => Promise<HTTPResponse<{ data: TResponse }>>
): ChatAPIEffect<TRequest, TResponse>

export function createChatAPIEffect<TRequest>(
    requestFn: (request: TRequest) => Promise<HTTPResponse>
): ChatAPIEffect<TRequest, any>

export function createChatAPIEffect(validatorOrRequestFn: any, requestFnOpt?: any): ChatAPIEffect<any, any> {
    const requestFn = requestFnOpt || validatorOrRequestFn
    const responseFn =
        'parse' in validatorOrRequestFn ? (value: any) => validatorOrRequestFn.parse(value) : (value: any) => value

    return createEffect<any, any, ChatAPIError>(async (payload) => {
        let response: any
        try {
            response = await requestFn(payload)
            if (response.data?.errors?.length > 0) {
                throw new ChatAPIError(ChatError.parse(response.data.errors[0]))
            }

            return responseFn(response.data.data)
        } catch (error) {
            if (error instanceof ChatAPIError) {
                throw error
            }

            if (error instanceof z.ZodError) {
                logger.error(
                    `[chat-api] invalid response: ${JSON.stringify(response)}, error: ${JSON.stringify(error)}`
                )

                throw new ChatAPIError(
                    {
                        code: 'invalid_response',
                        message: 'Invalid response',
                    },
                    { cause: error }
                )
            }

            if (error instanceof Error) {
                logger.error('[chat-api] transport error:', error.message)
                throw new ChatAPIError(
                    {
                        code: 'transport_error',
                        message: error.message,
                    },
                    { cause: error }
                )
            }

            logger.error('[chat-api] unknown error:', `${error}`)

            throw new ChatAPIError(
                {
                    code: 'unknown_error',
                    message: `${error}`,
                },
                { cause: error }
            )
        }
    })
}
